import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { API_URL, buildRequestPayload, type SearchResponse, type FlightItem } from "./esky";
import { expandMetro, isMetroCode } from "./airports";
import { TOTAL_BUDGET_MS, PER_ATTEMPT_TIMEOUT_MS } from "./search-timing";
import { logVerbose, logSummary, isVerbose as isVerboseLog } from "./search-log";

const SearchSchema = z.object({
  tripType: z.enum(["OneWay", "RoundTrip"]),
  from: z.string().length(3),
  to: z.string().length(3),
  departDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  returnDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(9),
  infants: z.number().int().min(0).max(9),
  serviceClass: z.enum(["economy", "economy-premium", "business", "first"]),
  directOnly: z.boolean(),
});

type SearchInput = z.infer<typeof SearchSchema>;

type EskyResult =
  | { ok: true; data: SearchResponse }
  | { ok: false; status?: number; error: string };

// In-memory cache pojedynczych wywołań eSky (per para lotnisk + parametry).
// TTL 5 min — wystarczająco dla "czasu sesji" (user przeklikuje filtry/sortowanie),
// a na tyle krótko, że ceny nie robią się stale. Dedup równoległych zapytań
// przez trzymanie Promise w mapie (drugi caller dostaje to samo Promise).
// TTL bardziej agresywne — ceny eSky rzadko zmieniają się minuta do minuty,
// a przy fan-oucie kraj→kraj każdy HIT oszczędza realne wywołanie do eSky.
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min — per-para lotnisk (success)
// Krótkie „negative cache" dla błędów: chroni przed lawiną duplikatów, gdy
// react-query po stronie klienta retry'uje failującą parę, albo gdy kilka
// fan-outów dotyka tej samej trasy w tym samym czasie. Po tym oknie wynik
// jest tracony, żeby następne wyszukiwanie miało szansę realnie ponowić.
const NEGATIVE_CACHE_TTL_MS = 3_000;
const METRO_CACHE_TTL_MS = 15 * 60 * 1000; // 15 min — gotowy zmergowany wynik
const MAX_ENTRIES = 1000;
// Adaptacyjny limit równoległych zapytań do eSky w jednym fan-oucie.
// Trzymamy się w przedziale [MIN, MAX]; faktyczna wartość liczy się przed
// każdym fan-outem na bazie rolling window ostatnich N prób — żeby przy
// objawach przeciążenia (429/5xx, wolne odpowiedzi) automatycznie zwolnić.
const FANOUT_CONCURRENCY_MAX = 16;
const FANOUT_CONCURRENCY_MIN = 4;

// Rolling window prób upstream — globalny dla procesu Workera. Każda próba
// (sukces lub błąd) zostaje zarejestrowana z czasem i kategorią statusu.
// Rozmiar 50 = wystarczająco świeże dane (kilka ostatnich wyszukiwań), a
// jednocześnie dość duża próbka, żeby p95 i error-rate nie skakały na 1-2
// próbach.
const ADAPT_WINDOW_SIZE = 50;
type AdaptSample = { ms: number; kind: "ok" | "429" | "5xx" | "other" };
const adaptWindow: AdaptSample[] = [];

function classifyStatus(s: EskyResult): AdaptSample["kind"] {
  if (s.ok) return "ok";
  const code = s.status;
  if (code === 429) return "429";
  if (code !== undefined && code >= 500 && code < 600) return "5xx";
  return "other";
}

function recordAdaptSample(sample: AdaptSample): void {
  adaptWindow.push(sample);
  if (adaptWindow.length > ADAPT_WINDOW_SIZE) adaptWindow.shift();
}

function computeAdaptiveConcurrency(): {
  limit: number;
  reason: string;
  errorRate: number;
  p95Ms: number;
  samples: number;
} {
  const n = adaptWindow.length;
  if (n < 8) {
    return { limit: FANOUT_CONCURRENCY_MAX, reason: "warmup", errorRate: 0, p95Ms: 0, samples: n };
  }
  let bad = 0;
  for (const s of adaptWindow) if (s.kind === "429" || s.kind === "5xx") bad++;
  const errorRate = bad / n;
  const sorted = adaptWindow.map((s) => s.ms).sort((a, b) => a - b);
  const p95Ms = sorted[Math.min(sorted.length - 1, Math.floor(0.95 * sorted.length))];

  // Reguły (kolejność od najostrzejszej): >30% błędów → twardo do MIN;
  // >15% błędów ALBO p95 > 12s → zmniejsz o połowę; p95 > 7s → -4; reszta MAX.
  let limit = FANOUT_CONCURRENCY_MAX;
  let reason = "healthy";
  if (errorRate > 0.3) {
    limit = FANOUT_CONCURRENCY_MIN;
    reason = `errorRate=${errorRate.toFixed(2)} (>30%)`;
  } else if (errorRate > 0.15 || p95Ms > 12_000) {
    limit = Math.max(FANOUT_CONCURRENCY_MIN, Math.floor(FANOUT_CONCURRENCY_MAX / 2));
    reason = `errorRate=${errorRate.toFixed(2)}, p95=${p95Ms}ms`;
  } else if (p95Ms > 7_000) {
    limit = Math.max(FANOUT_CONCURRENCY_MIN, FANOUT_CONCURRENCY_MAX - 4);
    reason = `p95=${p95Ms}ms (>7s)`;
  }
  return { limit, reason, errorRate, p95Ms, samples: n };
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return results;
}
const eskyCache = new Map<string, { at: number; promise: Promise<EskyResult>; ok?: boolean; empty?: boolean }>();

function cacheKey(input: SearchInput): string {
  return [
    input.tripType,
    input.from,
    input.to,
    input.departDate,
    input.returnDate ?? "",
    input.adults,
    input.children,
    input.infants,
    input.serviceClass,
    input.directOnly ? 1 : 0,
  ].join("|");
}

function entryTtl(v: { ok?: boolean; empty?: boolean }): number {
  // Promise jeszcze w locie (`ok === undefined`) traktujemy jak success —
  // chcemy żeby równoległe duplikaty zaczepiły się o ten sam Promise i nie
  // odpalały kolejnego callEskyRaw zanim pierwszy zdąży się rozstrzygnąć.
  if (v.ok === false) return NEGATIVE_CACHE_TTL_MS;
  // 200 OK ale 0 wyników (nawet po retry) — krótki TTL, żeby nie blokować
  // kolejnych userów na 30 min cached pustką. Trasa mogła być chwilowo
  // pusta po stronie eSky (cache miss, edge node) i odpowiedź naprawi się
  // przy następnym realnym wywołaniu.
  if (v.empty === true) return NEGATIVE_CACHE_TTL_MS;
  return CACHE_TTL_MS;
}

function pruneCache(now: number) {
  for (const [k, v] of eskyCache) {
    if (now - v.at > entryTtl(v)) eskyCache.delete(k);
  }
  if (eskyCache.size > MAX_ENTRIES) {
    // usuń najstarsze
    const sorted = Array.from(eskyCache.entries()).sort((a, b) => a[1].at - b[1].at);
    for (let i = 0; i < sorted.length - MAX_ENTRIES; i++) eskyCache.delete(sorted[i][0]);
  }
}

// Retry z exponential backoff dla błędów przejściowych (408, 429, 5xx, network).
// 3 próby łącznie (pierwsza + 2 retry). Backoff: 300ms, 900ms + jitter.
// 4xx inne niż 408/429 nie są retry'owane — to błąd zapytania, nie serwera.
const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 300;
// Sumaryczny budżet czasu na jedną parę lotnisk (wliczając wszystkie próby
// i backoffy) oraz twardy timeout pojedynczej próby pochodzą ze WSPÓLNEGO
// modułu `@/lib/search-timing` — dokładnie te same wartości widzi klient
// w queryFn. Bez wspólnego źródła klient anulowałby zapytanie zostawiając
// serwer mielący, albo serwer odpowiadałby błędem szybciej niż klient
// zdąży go zobaczyć i react-query retry tracił kontekst.

// Heurystyka „podejrzanie szybka pustka": eSky 200 OK z 0 itemami w czasie
// poniżej tego progu traktujemy jako transient bad response (cache miss po
// stronie eSky, edge fallback, itp.) i ponawiamy raz. Realne wyszukiwanie
// nawet dla niepopularnych tras trwa zwykle 2-15s; odpowiedź <1.2s z pustką
// to praktycznie zawsze artefakt po stronie providera — co potwierdziły
// realne logi (np. WMI→MNL: 376ms, 0 wyników, podczas gdy WAW→MNL: 4.9s,
// 400+ wyników w tej samej sekundzie).
const SUSPICIOUS_EMPTY_THRESHOLD_MS = 1_200;

function isRetriableStatus(status: number): boolean {
  return status === 408 || status === 429 || (status >= 500 && status < 600);
}

function isSuspiciousEmpty(r: EskyResult, elapsedMs: number): boolean {
  if (!r.ok) return false;
  if (elapsedMs >= SUSPICIOUS_EMPTY_THRESHOLD_MS) return false;
  const items = r.data.Items?.length ?? 0;
  const alt = r.data.AlternativeItems?.length ?? 0;
  return items === 0 && alt === 0;
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

async function callEskyOnce(input: SearchInput, timeoutMs: number): Promise<EskyResult> {
  const payload = buildRequestPayload(input);
  // AbortController zamiast Promise.race — pozwala REALNIE anulować socket
  // do eSky po deadline, zamiast tylko ignorować odpowiedź. Bez tego
  // Worker trzyma otwarty subrequest do końca i zjada budżet CPU/IO.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(new Error(`per-attempt timeout ${timeoutMs}ms`)), timeoutMs);
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      body: JSON.stringify(payload),
      signal: ac.signal,
    });
    if (!res.ok) {
      const text = await res.text();
      console.error("eSky API error", res.status, input.from, "->", input.to, text.slice(0, 200));
      return { ok: false, status: res.status, error: `http ${res.status}` };
    }
    const json = (await res.json()) as SearchResponse;
    return { ok: true, data: json };
  } catch (e) {
    const aborted = e instanceof Error && (e.name === "AbortError" || ac.signal.aborted);
    if (aborted) {
      logVerbose(`[eSky timeout] ${input.from}->${input.to} aborted after ${timeoutMs}ms`);
      // Brak `status` → traktowane przez `callEskyRaw` jako retriable
      // (analogicznie do network errora i 408).
      return { ok: false, error: `timeout ${timeoutMs}ms` };
    }
    console.error("eSky fetch failed", input.from, "->", input.to, e);
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  } finally {
    clearTimeout(timer);
  }
}

function statusLabel(r: EskyResult): string {
  if (r.ok) return "200";
  if (r.status !== undefined) return String(r.status);
  if (r.error.startsWith("timeout ")) return "timeout";
  return "network";
}

async function callEskyRaw(input: SearchInput, metrics?: Metrics): Promise<EskyResult> {
  const deadline = Date.now() + TOTAL_BUDGET_MS;
  const sid = metrics?.searchId ?? "-";
  const pair = `${input.from}->${input.to}`;
  const pStat = metrics ? getPair(metrics, input) : null;
  let last: EskyResult = { ok: false, error: "no attempts" };
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      logVerbose(`[eSky budget exhausted sid=${sid}] ${pair} before attempt ${attempt}`);
      const exhausted: EskyResult = !last.ok && last.error === "no attempts"
        ? { ok: false, error: `total timeout ${TOTAL_BUDGET_MS}ms` }
        : last;
      if (pStat) { pStat.finalOk = exhausted.ok; pStat.finalStatus = statusLabel(exhausted); }
      return exhausted;
    }
    // Per-attempt timeout = min(twardy limit, ile zostało z całego budżetu).
    // Ostatnia próba dostaje wszystko co zostało, ale nie więcej niż
    // PER_ATTEMPT_TIMEOUT_MS — żeby błąd zwrócił się w sensownym czasie.
    const attemptTimeout = Math.max(1_000, Math.min(PER_ATTEMPT_TIMEOUT_MS, remaining));
    if (metrics) metrics.attempts++;
    if (pStat) pStat.attempts++;
    const tAttempt = Date.now();
    last = await callEskyOnce(input, attemptTimeout);
    const elapsedMs = Date.now() - tAttempt;
    recordAdaptSample({ ms: elapsedMs, kind: classifyStatus(last) });
    const suspiciousEmpty = isSuspiciousEmpty(last, elapsedMs);
    const effectiveLabel = suspiciousEmpty ? "empty-fast" : statusLabel(last);
    if (pStat) pStat.statuses.push(effectiveLabel);
    if (last.ok && !suspiciousEmpty) {
      if (pStat) { pStat.finalOk = true; pStat.finalStatus = "200"; }
      return last;
    }
    // Network error / timeout (brak `status`) traktujemy jako przejściowy.
    // „empty-fast" (200 OK z 0 wynikami w <1.2s) też — to praktycznie zawsze
    // artefakt po stronie providera (cache miss eSky, edge fallback).
    const httpStatus = last.ok ? undefined : last.status;
    const retriable = suspiciousEmpty || httpStatus === undefined || isRetriableStatus(httpStatus);
    if (!retriable || attempt === MAX_ATTEMPTS) {
      if (pStat) { pStat.finalOk = last.ok; pStat.finalStatus = effectiveLabel; }
      return last;
    }
    if (metrics) metrics.retries++;
    const backoff = BASE_BACKOFF_MS * Math.pow(3, attempt - 1);
    const jitter = Math.floor(Math.random() * 150);
    const wait = backoff + jitter;
    // Nie czekaj, jeśli backoff przekroczyłby deadline — od razu wróć z
    // ostatnim błędem, zamiast spać i tak skończyć z budget exhausted.
    if (Date.now() + wait >= deadline) {
      logVerbose(`[eSky retry skipped sid=${sid}] ${pair} backoff ${wait}ms > remaining budget`);
      if (pStat) { pStat.finalOk = last.ok; pStat.finalStatus = effectiveLabel; }
      return last;
    }
    // Szczegółowy log dla 408 (HTTP 408 lub per-attempt timeout bez statusu).
    // Wyróżniamy go z ogólnego retry-loga, bo timeouty to najczęstsza przyczyna
    // zjedzenia budżetu i chcemy w logach widzieć dokładny backoff per próba.
    const is408 = httpStatus === 408 || effectiveLabel === "timeout";
    if (is408) {
      const remainingNow = deadline - Date.now();
      logVerbose(
        `[eSky retry-408 sid=${sid}] ${pair} attempt=${attempt}/${MAX_ATTEMPTS} ` +
          `cause=${effectiveLabel}${httpStatus === 408 ? " (http 408)" : ` (per-attempt ${PER_ATTEMPT_TIMEOUT_MS}ms)`} ` +
          `backoff=${backoff}ms jitter=${jitter}ms waitMs=${wait} ` +
          `budgetRemaining=${remainingNow}ms perAttemptTimeout=${PER_ATTEMPT_TIMEOUT_MS}ms ` +
          `priorStatuses=[${pStat?.statuses.join(",") ?? ""}]`,
      );
    }
    if (suspiciousEmpty) {
      logVerbose(
        `[eSky retry-empty sid=${sid}] ${pair} attempt ${attempt}/${MAX_ATTEMPTS} ` +
          `200 OK with 0 items in ${elapsedMs}ms (< ${SUSPICIOUS_EMPTY_THRESHOLD_MS}ms) — retry in ${wait}ms`,
      );
    } else {
      logVerbose(
        `[eSky retry sid=${sid}] ${pair} attempt ${attempt}/${MAX_ATTEMPTS} failed (${effectiveLabel}) — retry in ${wait}ms`,
      );
    }
    await sleep(wait);
  }
  if (pStat) { pStat.finalOk = last.ok; pStat.finalStatus = statusLabel(last); }
  return last;
}

// Zbieracz metryk dla jednego fan-outu — propagujemy go przez callEsky/retry,
// żeby na końcu wypluć jeden JSON-owy log nadający się do parsowania.
type PerPairStat = {
  attempts: number;
  statuses: string[]; // chronologicznie: ["429","cache","200"] / ["timeout","200"]
  finalOk: boolean;
  finalStatus: string; // "200" | "cache" | "429" | "timeout" | "network" | ...
};
type Metrics = {
  searchId: string; // unikalny ID całego fan-outu — pojawia się w KAŻDYM logu
  cacheHits: number;
  cacheMisses: number;
  attempts: number;
  retries: number;
  statusHistogram: Record<string, number>;
  callMs: number[]; // czas każdego callEsky (z cachem) — do p50/p95
  perPair: Map<string, PerPairStat>; // key: "FROM->TO"
};

function makeSearchId(): string {
  // 8-znakowy base36 z czasu + losowości — czytelny w logach, wystarczająco
  // unikalny dla okna ~1h (kolizja przy >36^8 ≈ 2.8B jednoczesnych searchów).
  return (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)).slice(-8);
}

function newMetrics(): Metrics {
  return {
    searchId: makeSearchId(),
    cacheHits: 0,
    cacheMisses: 0,
    attempts: 0,
    retries: 0,
    statusHistogram: {},
    callMs: [],
    perPair: new Map(),
  };
}

function pairKey(input: SearchInput): string {
  return `${input.from}->${input.to}`;
}

function getPair(m: Metrics, input: SearchInput): PerPairStat {
  const k = pairKey(input);
  let p = m.perPair.get(k);
  if (!p) {
    p = { attempts: 0, statuses: [], finalOk: false, finalStatus: "pending" };
    m.perPair.set(k, p);
  }
  return p;
}

function bumpStatus(m: Metrics, key: string) {
  m.statusHistogram[key] = (m.statusHistogram[key] ?? 0) + 1;
}

function percentile(arr: number[], p: number): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

// Serializowalna migawka metryk — wysyłana z każdą odpowiedzią serverFn,
// żeby klient mógł pokazać "Szczegóły techniczne" dla bieżącego wyszukiwania.
export type SearchMetricsSnapshot = {
  searchId: string;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  attempts: number;
  retries: number;
  totalMs: number;
  callMs: number[];
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  statusHistogram: Record<string, number>;
  pairs: number;
};

function metricsSnapshot(m: Metrics, totalMs: number): SearchMetricsSnapshot {
  const total = m.cacheHits + m.cacheMisses;
  return {
    searchId: m.searchId,
    cacheHits: m.cacheHits,
    cacheMisses: m.cacheMisses,
    hitRate: total > 0 ? +(m.cacheHits / total).toFixed(3) : 0,
    attempts: m.attempts,
    retries: m.retries,
    totalMs,
    callMs: m.callMs.slice(),
    p50Ms: percentile(m.callMs, 50),
    p95Ms: percentile(m.callMs, 95),
    maxMs: m.callMs.length ? Math.max(...m.callMs) : 0,
    statusHistogram: { ...m.statusHistogram },
    pairs: Math.max(1, m.perPair.size),
  };
}

async function callEsky(input: SearchInput, metrics?: Metrics): Promise<EskyResult> {
  const t0 = Date.now();
  const now = t0;
  const key = cacheKey(input);
  const hit = eskyCache.get(key);
  // Idempotencja per (from-to-date-...): jeśli identyczna para jest właśnie
  // w locie (`ok === undefined`) albo zakończyła się świeżym wynikiem
  // (success lub świeży błąd w oknie negative-cache), zwracamy ten sam
  // Promise zamiast spawnować kolejne wywołanie eSky. Eliminuje to:
  //  - duplikaty z react-query retry (client retry trafia jeszcze w okno),
  //  - duplikaty z fan-outu metro, w którym kilka kombinacji dat zbiega
  //    się do tej samej pary lotnisk po denormalizacji,
  //  - duplikaty z dwóch równoległych userów na tej samej trasie.
  if (hit && now - hit.at <= entryTtl(hit)) {
    if (metrics) metrics.cacheHits++;
    const r = await hit.promise;
    if (metrics) {
      metrics.callMs.push(Date.now() - t0);
      const label = r.ok ? "cache" : `cache-${r.status ?? "err"}`;
      bumpStatus(metrics, label);
      // Per-pair: cache hit nie zwiększa `attempts` (nie ruszyliśmy eSky),
      // ale zapisujemy status, żeby w podsumowaniu było widać że ta para
      // została obsłużona z cache.
      const pStat = getPair(metrics, input);
      pStat.statuses.push(label);
      pStat.finalOk = r.ok;
      pStat.finalStatus = label;
    }
    return r;
  }
  if (metrics) metrics.cacheMisses++;
  const entry: { at: number; promise: Promise<EskyResult>; ok?: boolean; empty?: boolean } = {
    at: now,
    promise: undefined as unknown as Promise<EskyResult>,
  };
  entry.promise = callEskyRaw(input, metrics).then((r) => {
    // Oznaczamy wynik na wpisie (success → pełny TTL, błąd → krótki
    // negative-cache TTL). NIE kasujemy błędów natychmiast — gdybyśmy
    // kasowali, retry react-query trafiłby w cache-miss i zrobił kolejne
    // realne wywołanie eSky w tej samej sekundzie.
    entry.ok = r.ok;
    entry.at = Date.now();
    if (r.ok) {
      const items = r.data.Items?.length ?? 0;
      const alt = r.data.AlternativeItems?.length ?? 0;
      entry.empty = items === 0 && alt === 0;
    }
    return r;
  });

  eskyCache.set(key, entry);
  pruneCache(now);
  const r = await entry.promise;
  if (metrics) {
    metrics.callMs.push(Date.now() - t0);
    bumpStatus(metrics, r.ok ? "200" : String(r.status ?? "network"));
  }
  return r;
}

// Cache na poziomie CAŁEGO zapytania metro (np. LON→PAR jako jedna jednostka,
// nie tylko per LHR→CDG). Trzymamy gotową, zmergowaną odpowiedź razem z
// `warning` i `meta`, żeby drugi user (lub ten sam, przy zmianie filtrów /
// sortowania / cofnięciu z karty oferty) dostał wynik bez żadnego wywołania
// eSky. TTL i dedup równoległych zapytań analogicznie do `eskyCache`.
//
// Round-trip ma OSOBNY cache (`rtCache`) z kluczem niezależnym od kierunku:
// LON↔PAR(D1↔D2) i PAR↔LON(D2↔D1) to ta sama zamknięta podróż, więc dzielą
// wynik. Dzięki temu po wyszukaniu LON→PAR(15-22 mar) reverse PAR→LON(22-15 mar)
// nie woła ponownie eSky. One-way trzymamy w `metroCache` jak dotąd.
type MetroResult = Awaited<ReturnType<typeof runSearch>>;
const metroCache = new Map<string, { at: number; promise: Promise<MetroResult> }>();
const rtCache = new Map<string, { at: number; promise: Promise<MetroResult> }>();

function metroCacheKey(input: SearchInput): string {
  return cacheKey(input);
}

/**
 * Klucz dla round-tripu niezależny od kierunku. Sortujemy parę miast
 * alfabetycznie i przypisujemy daty tak, żeby depart zawsze odnosił się
 * do tego samego końca trasy. Dla one-way wracamy do zwykłego klucza.
 */
function rtCacheKey(input: SearchInput): string {
  if (input.tripType !== "RoundTrip" || !input.returnDate) return cacheKey(input);
  const [from, to, departDate, returnDate] =
    input.from <= input.to
      ? [input.from, input.to, input.departDate, input.returnDate]
      : [input.to, input.from, input.returnDate, input.departDate];
  return cacheKey({ ...input, from, to, departDate, returnDate });
}

function pickCache(input: SearchInput) {
  return input.tripType === "RoundTrip" && input.returnDate
    ? { map: rtCache, key: rtCacheKey(input), label: "rt" }
    : { map: metroCache, key: metroCacheKey(input), label: "ow" };
}

// Łączymy wyniki z N par lotnisk w jeden SearchResponse:
//  - dedup po FID (każdy lot pojawia się raz, nawet gdy eSky zwróci go w kilku
//    odpowiedziach — przy metro to się zdarza dla połączeń z przesiadką),
//  - sort rosnąco po cenie (P),
//  - SearchFilters łączymy: TransferCodes / AirlineCodes jako unię,
//    MinimalPrice/MaximalPrice jako globalne min/max,
//  - MoreResultsAvailable = OR po wszystkich.
export function mergeResponses(parts: SearchResponse[]): SearchResponse {
  const byId = new Map<string, FlightItem>();
  const altById = new Map<string, FlightItem>();
  const transfer = new Set<string>();
  const airlines = new Set<string>();
  const airportNames = new Map<string, { Key: string; Type: string; Value: string }>();
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  let minTime: string | undefined;
  let maxTime: string | undefined;
  let more = false;

  for (const r of parts) {
    for (const it of r.Items ?? []) if (!byId.has(it.FID)) byId.set(it.FID, it);
    for (const it of r.AlternativeItems ?? []) if (!altById.has(it.FID) && !byId.has(it.FID)) altById.set(it.FID, it);
    const f = r.SearchFilters;
    if (f) {
      f.TransferCodes?.forEach((c) => transfer.add(c));
      f.AirlineCodes?.forEach((c) => airlines.add(c));
      f.AirportsNames?.forEach((n) => airportNames.set(n.Key, n));
      if (typeof f.MinimalPrice === "number" && f.MinimalPrice < minPrice) minPrice = f.MinimalPrice;
      if (typeof f.MaximalPrice === "number" && f.MaximalPrice > maxPrice) maxPrice = f.MaximalPrice;
      if (f.MinimalFlightTime && (!minTime || f.MinimalFlightTime < minTime)) minTime = f.MinimalFlightTime;
      if (f.MaximalFlightTime && (!maxTime || f.MaximalFlightTime > maxTime)) maxTime = f.MaximalFlightTime;
    }
    if (r.MoreResultsAvailable) more = true;
  }

  const sortByPrice = (a: FlightItem, b: FlightItem) => (a.P ?? Infinity) - (b.P ?? Infinity);
  return {
    Items: Array.from(byId.values()).sort(sortByPrice),
    AlternativeItems: Array.from(altById.values()).sort(sortByPrice),
    SearchFilters: {
      TransferCodes: Array.from(transfer),
      AirlineCodes: Array.from(airlines),
      AirportsNames: Array.from(airportNames.values()),
      MinimalPrice: Number.isFinite(minPrice) ? minPrice : undefined,
      MaximalPrice: Number.isFinite(maxPrice) ? maxPrice : undefined,
      MinimalFlightTime: minTime,
      MaximalFlightTime: maxTime,
    },
    MoreResultsAvailable: more,
  };
}

async function runSearch(data: SearchInput) {
  const fromCodes = expandMetro(data.from);
  const toCodes = expandMetro(data.to);
  const isMetroSearch = isMetroCode(data.from) || isMetroCode(data.to);
  const metrics = newMetrics();
  const tStart = Date.now();
  const sid = metrics.searchId;

  // Serializator perPair → tablica obiektów { pair, attempts, statuses,
  // finalOk, finalStatus } posortowanych: najpierw padłe, potem retry'owane,
  // potem czyste 200. Dzięki temu od razu widać które pary były problematyczne.
  const dumpPerPair = () =>
    Array.from(metrics.perPair.entries())
      .map(([pair, p]) => ({
        pair,
        attempts: p.attempts,
        statuses: p.statuses,
        finalOk: p.finalOk,
        finalStatus: p.finalStatus,
      }))
      .sort((a, b) =>
        a.finalOk === b.finalOk
          ? b.attempts - a.attempts
          : a.finalOk ? 1 : -1,
      );

  // Szybka ścieżka: jedno lotnisko po obu stronach — bez fan-outu.
  if (fromCodes.length === 1 && toCodes.length === 1) {
    const r = await callEsky({ ...data, from: fromCodes[0], to: toCodes[0] }, metrics);
    const totalMs = Date.now() - tStart;
    logSummary(
      `[searchFlights.metrics] ${JSON.stringify({
        searchId: sid,
        route: `${data.from}->${data.to}`,
        pairs: 1,
        ok: r.ok ? 1 : 0,
        failed: r.ok ? 0 : 1,
        totalMs,
        cacheHits: metrics.cacheHits,
        cacheMisses: metrics.cacheMisses,
        hitRate: metrics.cacheHits + metrics.cacheMisses > 0
          ? +(metrics.cacheHits / (metrics.cacheHits + metrics.cacheMisses)).toFixed(2)
          : 0,
        attempts: metrics.attempts,
        retries: metrics.retries,
        status: metrics.statusHistogram,
        // perPair tylko w trybie verbose — w summary nie zalewamy logów
        // szczegółami pojedynczych par.
        ...(isVerboseLog() ? { perPair: dumpPerPair() } : {}),
      })}`,
    );
    const snap = metricsSnapshot(metrics, totalMs);
    if (!r.ok) {
      return {
        ok: false as const,
        error: `Wyszukiwarka eSky zwróciła błąd${r.status ? ` (${r.status})` : ""}. Spróbuj ponownie za chwilę.`,
        data: null,
        metrics: snap,
      };
    }
    return { ok: true as const, error: null, data: r.data, warning: null, metrics: snap };
  }

  // Fan-out — bez limitu par. Każdy from × każde to.
  const pairs: Array<[string, string]> = fromCodes.flatMap((f) =>
    toCodes.map((t) => [f, t] as [string, string]),
  );
  const adapt = computeAdaptiveConcurrency();
  logVerbose(
    `[searchFlights sid=${sid}] metro fan-out start: ${data.from}->${data.to} = ${pairs.length} pairs, concurrency=${adapt.limit}/${FANOUT_CONCURRENCY_MAX} (${adapt.reason}, samples=${adapt.samples}, errorRate=${adapt.errorRate.toFixed(2)}, p95=${adapt.p95Ms}ms) (${fromCodes.join(",")} × ${toCodes.join(",")})`,
  );
  const settled = await mapWithConcurrency(pairs, adapt.limit, ([f, t]) =>
    callEsky({ ...data, from: f, to: t }, metrics),
  );
  const successes: SearchResponse[] = [];
  const failedPairs: Array<{ from: string; to: string; status?: number; error: string }> = [];
  settled.forEach((r, i) => {
    const [f, t] = pairs[i];
    if (r.ok) successes.push(r.data);
    else failedPairs.push({ from: f, to: t, status: r.status, error: r.error });
  });
  const totalMs = Date.now() - tStart;
  const totalCalls = metrics.cacheHits + metrics.cacheMisses;
  logSummary(
    `[searchFlights.metrics] ${JSON.stringify({
      searchId: sid,
      route: `${data.from}->${data.to}`,
      pairs: pairs.length,
      ok: successes.length,
      failed: failedPairs.length,
      totalMs,
      cacheHits: metrics.cacheHits,
      cacheMisses: metrics.cacheMisses,
      hitRate: totalCalls > 0 ? +(metrics.cacheHits / totalCalls).toFixed(2) : 0,
      attempts: metrics.attempts,
      retries: metrics.retries,
      status: metrics.statusHistogram,
      p50Ms: percentile(metrics.callMs, 50),
      p95Ms: percentile(metrics.callMs, 95),
      maxMs: metrics.callMs.length ? Math.max(...metrics.callMs) : 0,
      ...(isVerboseLog() ? { perPair: dumpPerPair() } : {}),
    })}`,
  );

  const snap = metricsSnapshot(metrics, totalMs);

  if (successes.length === 0) {
    return {
      ok: false as const,
      error: "Wyszukiwarka eSky nie odpowiada. Spróbuj ponownie za chwilę.",
      data: null,
      metrics: snap,
    };
  }

  let warning: string | null = null;
  if (failedPairs.length > 0) {
    const routes = failedPairs.map((p) => `${p.from}→${p.to}`).join(", ");
    warning =
      `Sprawdziliśmy ${successes.length} z ${pairs.length} ` +
      `${pairs.length === 1 ? "trasy" : "tras"} dla wybranych miast. ` +
      `Wyniki dla ${failedPairs.length === 1 ? "trasy" : "tras"} ${routes} ` +
      `są chwilowo niedostępne — możesz spróbować ponownie za moment.`;
  }

  return {
    ok: true as const,
    error: null,
    data: mergeResponses(successes),
    warning,
    meta: {
      metro: isMetroSearch,
      pairs: pairs.length,
      succeeded: successes.length,
      failed: failedPairs.length,
      failedPairs,
    },
    metrics: snap,
  };
}

async function runSearchCached(data: SearchInput): Promise<MetroResult> {
  const now = Date.now();
  const { map, key, label } = pickCache(data);
  const hit = map.get(key);
  if (hit && now - hit.at <= METRO_CACHE_TTL_MS) {
    const ageS = Math.round((now - hit.at) / 1000);
    // Świadomy „join" — kilku userów (lub ten sam po F5) trafia w ten sam
    // in-flight Promise. To kluczowy mechanizm odporności na hard-reloady:
    // pierwsza próba populuje Promise w cache, drugi reload tylko czeka na
    // jego rozstrzygnięcie (zero dodatkowych wywołań eSky).
    logVerbose(
      `[searchFlights.cache] HIT ${label} ${data.from}->${data.to} age=${ageS}s size=${map.size}`,
    );
    return hit.promise;
  }
  logVerbose(
    `[searchFlights.cache] MISS ${label} ${data.from}->${data.to} size=${map.size}`,
  );
  // KLUCZOWE: `map.set` musi się stać PRZED `await promise`. Dzięki temu
  // Promise żyje dalej, nawet jeśli aktualny request HTTP zostanie zerwany
  // przez klienta (np. user kliknął F5). Cloudflare Workers kontynuują
  // wykonanie zadań w event-loopie tak długo, jak coś trzyma do nich
  // referencję — a `metroCache` trzyma. Następny request na ten sam klucz
  // dostanie ten sam Promise i poczeka na jego ukończenie, zamiast odpalać
  // nowy fan-out do eSky.
  const promise = runSearch(data).then((r) => {
    if (!r.ok) map.delete(key);
    return r;
  });
  map.set(key, { at: now, promise });
  for (const [k, v] of map) {
    if (now - v.at > METRO_CACHE_TTL_MS) map.delete(k);
  }
  if (map.size > MAX_ENTRIES) {
    const sorted = Array.from(map.entries()).sort((a, b) => a[1].at - b[1].at);
    for (let i = 0; i < sorted.length - MAX_ENTRIES; i++) map.delete(sorted[i][0]);
  }
  return promise;
}

// Eksport do testów regresji.
export const __caches = { metroCache, rtCache, eskyCache, rtCacheKey, metroCacheKey, runSearchCached, pickCache };
export const __retry = {
  callEskyRaw,
  MAX_ATTEMPTS,
  BASE_BACKOFF_MS,
  PER_ATTEMPT_TIMEOUT_MS,
  TOTAL_BUDGET_MS,
};

export const searchFlights = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => SearchSchema.parse(input))
  .handler(async ({ data }) => runSearchCached(data));

